import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Button } from '../components/Button'
import { Card } from '../components/Card'
import { ToastProvider, useToast } from '../components/Toast'
import { useDb, getRoleHomePath, getSessionUser } from '../state/DbProvider'
import { superAgencies } from '../state/selectors'

function AgencySignupInner() {
  const { db, actions, loading } = useDb()
  const toast = useToast()
  const navigate = useNavigate()
  const location = useLocation()

  const [companyName, setCompanyName] = useState('')
  const [contactName, setContactName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [website, setWebsite] = useState('')
  const [note, setNote] = useState('')
  const [selectedSuperAgencyId, setSelectedSuperAgencyId] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const already = db ? getSessionUser(db) : null

  const inviteCode = useMemo(() => {
    const sp = new URLSearchParams(location.search)
    return (sp.get('code') ?? '').trim().toUpperCase()
  }, [location.search])

  const superAgencyList = useMemo(() => (db ? superAgencies(db) : []), [db])
  const invitedSuperAgency = useMemo(() => {
    if (!db || !inviteCode) return null
    return (
      superAgencyList.find((agency) => (agency.agency?.inviteCode ?? '').toUpperCase() === inviteCode) ?? null
    )
  }, [db, inviteCode, superAgencyList])

  useEffect(() => {
    if (already) navigate(getRoleHomePath(already), { replace: true })
  }, [already, navigate])

  useEffect(() => {
    if (invitedSuperAgency) {
      setSelectedSuperAgencyId(invitedSuperAgency.id)
    }
  }, [invitedSuperAgency])

  if (already) return null

  const hasInviteFlow = inviteCode.length > 0
  const invalidInvite = hasInviteFlow && !loading && !invitedSuperAgency
  const canSubmit =
    !!companyName.trim()
    && !!contactName.trim()
    && !!email.trim()
    && !!phone.trim()
    && !!selectedSuperAgencyId
    && !invalidInvite

  return (
    <div className="mx-auto max-w-2xl">
      <Card title="代理店申請" subtitle="所属する総代理店を指定して、J-Navi の代理店アカウント申請を受け付けます">
        <div className="grid gap-4 px-5 py-4">
          {submitted ? (
            <div className="grid gap-3">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
                代理店申請を受け付けました。審査後、承認担当者からご連絡します。
              </div>
              <div className="flex flex-col gap-2 text-sm sm:flex-row sm:items-center sm:justify-between">
                <Link to="/login" className="text-blue-600 hover:underline">
                  ログインへ戻る
                </Link>
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  className="text-blue-600 hover:underline"
                >
                  別の内容で申請する
                </button>
              </div>
            </div>
          ) : (
            <>
              {hasInviteFlow ? (
                loading ? (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
                    招待コードを確認しています...
                  </div>
                ) : invalidInvite ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                    招待コードが無効です。総代理店から最新の招待リンクを受け取ってください。
                  </div>
                ) : invitedSuperAgency ? (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                    所属先総代理店: <span className="font-semibold">{invitedSuperAgency.name}</span>
                  </div>
                ) : null
              ) : (
                <label className="grid gap-1 text-sm">
                  <span className="text-slate-700">所属する総代理店</span>
                  <select
                    value={selectedSuperAgencyId}
                    onChange={(event) => setSelectedSuperAgencyId(event.target.value)}
                    className="rounded-lg border border-slate-200 bg-white px-3 py-2"
                    disabled={loading}
                  >
                    <option value="">選択してください</option>
                    {superAgencyList.map((agency) => (
                      <option key={agency.id} value={agency.id}>
                        {agency.name}
                      </option>
                    ))}
                  </select>
                  <span className="text-xs text-slate-500">
                    公開導線では総代理店を選んで申請します。招待リンク経由の場合は所属先が固定されます。
                  </span>
                </label>
              )}

              <div className="grid gap-3 md:grid-cols-2">
                <label className="grid gap-1 text-sm md:col-span-2">
                  <span className="text-slate-700">会社名もしくは個人事業主名</span>
                  <input
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2"
                    placeholder="株式会社サンプル / 山田商店"
                  />
                </label>
                <label className="grid gap-1 text-sm">
                  <span className="text-slate-700">担当者名</span>
                  <input
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2"
                    placeholder="山田 花子"
                  />
                </label>
                <label className="grid gap-1 text-sm">
                  <span className="text-slate-700">電話番号</span>
                  <input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2"
                    placeholder="03-1234-5678"
                    type="tel"
                  />
                </label>
                <label className="grid gap-1 text-sm md:col-span-2">
                  <span className="text-slate-700">メールアドレス</span>
                  <input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2"
                    placeholder="contact@example.com"
                    type="email"
                  />
                </label>
                <label className="grid gap-1 text-sm md:col-span-2">
                  <span className="text-slate-700">Webサイト</span>
                  <input
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2"
                    placeholder="https://example.com"
                    type="url"
                  />
                </label>
                <label className="grid gap-1 text-sm md:col-span-2">
                  <span className="text-slate-700">備考</span>
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    className="min-h-28 w-full rounded-lg border border-slate-200 bg-white px-3 py-2"
                    placeholder="取り扱い商材や営業体制などがあればご記入ください"
                  />
                </label>
              </div>

              <Button
                variant="primary"
                disabled={!canSubmit}
                onClick={async () => {
                  const res = await actions.submitAgencySignupRequest({
                    companyName,
                    contactName,
                    email,
                    phone,
                    superAgencyId: selectedSuperAgencyId,
                    website,
                    note,
                  })

                  if (!res.ok) {
                    toast.show(res.message ?? '代理店申請を送信できませんでした', 'error')
                    return
                  }

                  toast.show('代理店申請を受け付けました', 'success')
                  setSubmitted(true)
                }}
              >
                代理店申請を送信
              </Button>

              <div className="flex flex-col gap-2 text-sm sm:flex-row sm:items-center sm:justify-between">
                <Link to="/login" className="text-blue-600 hover:underline">
                  ログインへ
                </Link>
                <span className="text-slate-500">コネクター登録は代理店または総代理店の招待URLから行ってください。</span>
              </div>
            </>
          )}
        </div>
      </Card>
    </div>
  )
}

export default function AgencySignupPage() {
  return (
    <ToastProvider>
      <AgencySignupInner />
    </ToastProvider>
  )
}
